import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, ShoppingCart,
  Eye, MousePointer, Target, RefreshCw, Loader2, ArrowUpRight,
  ArrowDownRight, Store
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#f97316', '#22c55e', '#3b82f6', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#eab308', '#14b8a6', '#f43f5e'];

const MarketplaceAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('receita');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/analytics/marketplace`);
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
      toast.error('Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!data) return null;

  const { marketplaces, resumo } = data;

  const chartData = marketplaces.map(m => ({
    name: m.marketplace.length > 12 ? m.marketplace.substring(0, 12) + '...' : m.marketplace,
    receita: m.receita,
    vendas: m.vendas,
    comissao: m.comissao
  }));

  const pieData = marketplaces.map((m, i) => ({
    name: m.marketplace,
    value: m.receita,
    fill: COLORS[i % COLORS.length]
  }));

  return (
    <div className="p-4 lg:p-6 bg-gray-950 overflow-auto h-full" data-testid="marketplace-analytics">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <img src="/images/clickhub-logo.png" alt="ClickHub" className="w-6 h-6 object-contain" />
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase">Analytics</span>
              <h1 data-testid="analytics-title" className="text-xl font-bold text-white">Performance por Marketplace</h1>
            </div>
            <p className="text-[11px] text-gray-400">Acompanhe suas vendas e comissoes em cada plataforma</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadAnalytics} className="border-gray-700" data-testid="refresh-analytics-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* KPIs Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 border-none" data-testid="kpi-receita">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-xs">Receita Total</p>
                <p className="text-white text-xl font-bold">R$ {(resumo.total_receita / 1000).toFixed(0)}k</p>
              </div>
              <DollarSign className="w-8 h-8 text-white/50" />
            </div>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 p-3 border-none" data-testid="kpi-vendas">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-xs">Total Vendas</p>
                <p className="text-white text-xl font-bold">{resumo.total_vendas.toLocaleString()}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-white/50" />
            </div>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 border-none" data-testid="kpi-comissao">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-xs">Comissoes</p>
                <p className="text-white text-xl font-bold">R$ {(resumo.total_comissao / 1000).toFixed(0)}k</p>
              </div>
              <TrendingUp className="w-8 h-8 text-white/50" />
            </div>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-violet-600 p-3 border-none" data-testid="kpi-marketplaces">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-xs">Marketplaces Ativos</p>
                <p className="text-white text-xl font-bold">{resumo.total_marketplaces}</p>
                <p className="text-purple-200 text-xs">Top: {resumo.melhor_marketplace}</p>
              </div>
              <Store className="w-8 h-8 text-white/50" />
            </div>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Bar Chart */}
          <Card className="bg-gray-800/50 border-gray-700 p-4 lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-500" />
                {view === 'receita' ? 'Receita por Marketplace' : view === 'vendas' ? 'Vendas por Marketplace' : 'Comissoes por Marketplace'}
              </h3>
              <div className="flex gap-1">
                {['receita', 'vendas', 'comissao'].map(v => (
                  <Button
                    key={v}
                    size="sm"
                    onClick={() => setView(v)}
                    className={`h-6 text-[10px] ${view === v ? 'bg-orange-500' : 'bg-gray-700'}`}
                  >
                    {v === 'receita' ? 'Receita' : v === 'vendas' ? 'Vendas' : 'Comissao'}
                  </Button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value) => view === 'vendas' ? value : `R$ ${value.toLocaleString()}`}
                />
                <Bar
                  dataKey={view}
                  fill="#f97316"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Pie Chart */}
          <Card className="bg-gray-800/50 border-gray-700 p-4">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-500" />
              Distribuicao de Receita
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={pieData.slice(0, 5)}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.slice(0, 5).map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  formatter={(value) => `R$ ${value.toLocaleString()}`}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {pieData.slice(0, 5).map((item, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.fill }} />
                    <span className="text-gray-300">{item.name}</span>
                  </div>
                  <span className="text-gray-400">{((item.value / resumo.total_receita) * 100).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Tabela de Marketplaces */}
        <Card className="bg-gray-800/50 border-gray-700 p-4">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Store className="w-5 h-5 text-orange-500" />
            Detalhamento por Marketplace
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 py-2 px-2 text-xs">#</th>
                  <th className="text-left text-gray-400 py-2 px-2 text-xs">Marketplace</th>
                  <th className="text-right text-gray-400 py-2 px-2 text-xs">Vendas</th>
                  <th className="text-right text-gray-400 py-2 px-2 text-xs">Receita</th>
                  <th className="text-right text-gray-400 py-2 px-2 text-xs">Ticket Medio</th>
                  <th className="text-right text-gray-400 py-2 px-2 text-xs">Comissao</th>
                  <th className="text-right text-gray-400 py-2 px-2 text-xs">Conversao</th>
                  <th className="text-right text-gray-400 py-2 px-2 text-xs">Crescimento</th>
                </tr>
              </thead>
              <tbody>
                {marketplaces.map((mp, i) => (
                  <tr key={mp.marketplace_id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors" data-testid={`mp-row-${mp.marketplace_id}`}>
                    <td className="py-2.5 px-2">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: COLORS[i % COLORS.length] }}>
                        {i + 1}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-white font-medium">{mp.marketplace}</td>
                    <td className="py-2.5 px-2 text-right text-gray-300">{mp.vendas.toLocaleString()}</td>
                    <td className="py-2.5 px-2 text-right text-green-400 font-semibold">R$ {mp.receita.toLocaleString()}</td>
                    <td className="py-2.5 px-2 text-right text-gray-300">R$ {mp.ticket_medio.toFixed(2)}</td>
                    <td className="py-2.5 px-2 text-right text-orange-400">R$ {mp.comissao.toLocaleString()}</td>
                    <td className="py-2.5 px-2 text-right text-blue-400">{mp.conversao}%</td>
                    <td className="py-2.5 px-2 text-right">
                      <span className={`flex items-center justify-end gap-0.5 ${mp.crescimento >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {mp.crescimento >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {Math.abs(mp.crescimento)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
    </div>
  );
};

export default MarketplaceAnalytics;
